import { createContext, useContext } from 'react';
import { useFocusRing } from '@react-aria/focus';
import { FocusableCheckboxIcon } from '../Checkbox/FocusableCheckboxIcon';
import {
  GroupField,
  GroupFieldItem,
  validGroupFieldProps,
} from '../GroupField/GroupField';
import {
  CheckboxGroupItemProps,
  CheckboxGroupProps,
  CheckboxGroupState,
} from './types';
import { useGroupState } from '../../groupUtils';
import { pick, uniq, without } from 'lodash';

const CheckboxGroupContext = createContext<CheckboxGroupState>({});

export const CheckboxGroup = ({ children, ...props }: CheckboxGroupProps) => {
  const { options = [] } = props;
  const state = useGroupState(props);
  return (
    <CheckboxGroupContext.Provider value={state}>
      <GroupField {...pick(props, validGroupFieldProps)}>
        {options.map(({ label, ...rest }) => (
          <CheckboxGroupItem key={rest.value} {...rest}>
            {label}
          </CheckboxGroupItem>
        ))}
        {children}
      </GroupField>
    </CheckboxGroupContext.Provider>
  );
};

export const CheckboxGroupItem = ({
  isDisabled: isDisabledOption,
  children,
  value,
  dataTestId,
  ariaLabel,
}: CheckboxGroupItemProps) => {
  const {
    isDisabled: isDisabledField,
    setCurrentValue,
    currentValue,
    hasError,
    hasWarning,
  } = useContext(CheckboxGroupContext);
  const { isFocusVisible, focusProps } = useFocusRing();

  const isDisabled = isDisabledField || isDisabledOption;
  const currentGroupValue = currentValue || [];
  const toggleCheckboxValue = () => {
    const isCurrentItemSelected = currentGroupValue.includes(value);
    setCurrentValue?.(
      isCurrentItemSelected
        ? without(currentGroupValue, value)
        : uniq([...currentGroupValue, value])
    );
  };
  const isChecked = currentGroupValue.includes(value);
  return (
    <GroupFieldItem
      onClick={() => toggleCheckboxValue()}
      $isDisabled={isDisabled}
      $isActive={isChecked}
      $hasError={hasError}
      $hasWarning={hasWarning}
      tabIndex={isDisabled ? -1 : 0}
      {...focusProps}
      onKeyDown={e => {
        if (e.key === ' ') {
          e.preventDefault();
          toggleCheckboxValue();
        }
      }}
      data-test-id={dataTestId}
      aria-label={ariaLabel}
    >
      <FocusableCheckboxIcon isChecked={isChecked} isFocused={isFocusVisible} />
      {children}
    </GroupFieldItem>
  );
};
